import { Alert, Box, Button, IconButton, Snackbar, type SnackbarProps, Typography } from '@mui/joy'
import { type FC, type MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { TbX } from 'react-icons/tb'

import CarambolaIcon from '../../assets/img/carambola.svg?react'
import { openChatwoot } from '../../components/chatwoot/chatwootApi'
import { type FeedbackRoute } from '../../hooks/useFeedbackPrompt/useFeedbackPrompt'
import { SHOPIFY_APP_REVIEW_URL } from '../../utils/constants'
import { SNACKBAR_SX } from './snackbarSx'

export interface FeedbackSnackbarProps extends SnackbarProps {
  onAnswer: (route?: FeedbackRoute) => void
}

// The design asks for Joy's default neutral soft surface. This app restyles a
// neutral Alert to a blue tint (src/themes/light.tsx), so the two values the
// design specifies are set here explicitly. See decisions.md D-7.
const SURFACE = '#f0f4f8'
const INK = '#32383e'
const MAX_HEIGHT = 69

const FeedbackSnackbar: FC<FeedbackSnackbarProps> = ({ onAnswer, onClose, ...props }) => {
  const { t } = useTranslation()

  // The container owns visibility through onClose, so every control has to
  // close as well as report — otherwise the alert reports and stays put.
  const finish =
    (route?: FeedbackRoute) =>
    (event: MouseEvent<HTMLElement>): void => {
      // Called with no argument when there is no route, so the callback can
      // tell "closed" from "chose nothing".
      if (route === undefined) {
        onAnswer()
      } else {
        onAnswer(route)
      }

      onClose?.(event, 'escapeKeyDown')
    }

  return (
    <Snackbar
      {...props}
      onClose={onClose}
      variant="soft"
      sx={SNACKBAR_SX}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <Alert
        variant="soft"
        sx={{
          width: '100%',
          boxSizing: 'border-box',
          maxHeight: { sm: MAX_HEIGHT },
          alignItems: 'center',
          gap: 1.25,
          px: 1.5,
          py: { xs: 1.5, sm: 1 },
          bgcolor: SURFACE,
          color: INK,
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
        }}
        startDecorator={<CarambolaIcon aria-hidden="true" width={24} height={24} />}
        endDecorator={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Button
              size="sm"
              variant="outlined"
              color="neutral"
              onClick={(event) => {
                openChatwoot()
                finish('contact')(event)
              }}
            >
              {t('feedback.contact.cta')}
            </Button>
            <Button
              size="sm"
              component="a"
              href={SHOPIFY_APP_REVIEW_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={finish('review')}
            >
              {t('feedback.review.cta')}
            </Button>
            <IconButton
              size="xs"
              variant="plain"
              color="neutral"
              aria-label={t('feedback.dismiss')}
              onClick={finish()}
            >
              <TbX size={20} />
            </IconButton>
          </Box>
        }
      >
        <Box>
          <Typography level="title-md" sx={{ color: INK, fontWeight: 600 }}>
            {t('feedback.title')}
          </Typography>
          <Typography level="body-sm" sx={{ color: INK, fontWeight: 500 }}>
            {t('feedback.body')}
          </Typography>
        </Box>
      </Alert>
    </Snackbar>
  )
}

export default FeedbackSnackbar
